import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { isPagelessRoute } from '../router/routeUtils'
import { suggest } from '../../store/suggest/suggest.actions'
import { setCurrentSearch } from '../../store/ui/ui.actions'
import { navigateTo } from '../../store/history/history.actions'
import s from './SearchInput.less'
import Throbber from '../loading/Throbber'
import classNames from 'classnames'
import { ROUTE_SUGGEST } from '../../routes'
import { selectCurrentHistoryRoute, selectIsHomePage } from '../../store/history/history.selectors'
import { useAction } from '../utils/react-redux'
import useDebounce from '../utils/useDebounce'
import { usePrevious } from '../utils/usePrevious'
import { isGE } from '../Device'
import TagActionButton from '../genericComponents/TagActionButton'
import { PUB_TAG } from '../analytics/PublisherTagsService'
import { TAG_PUBLISHER } from '../../store/analytics/analytics.actionTypes'
import QuickSearchInput from './QuickSearchInput'
import SearchInputIcon from '../search/SearchInputIcon'
import { selectHasSearchIcon } from '../../store/poiassets/poiassets.selectors'
import useUIDisplay from '../ui/useUIDisplay'
import { UI_ELEMENTS } from '../../store/ui/ui.constants'

const SearchInput = forwardRef((props, ref) => {
  const {
    value,
    loading,
    onChange,
    onFocus,
    suggestFilterType,
    className,
    placeholder,
    idx = 0,
    prefix,
    hasGreenPlaceholder = false,
    navigateToSuggestOnFocus = true,
    isFirstStepToFill = true
  } = props

  const [hasFocus, setFocus] = useState(false)
  const route = useSelector(selectCurrentHistoryRoute)
  const hasIcon = useSelector(selectHasSearchIcon)
  const prevRoute = usePrevious(route)
  const execSuggest = useAction(suggest)
  const execSetCurrentSearch = useAction(setCurrentSearch)
  const execNavigateTo = useAction(navigateTo)
  const [isReady, setIsReady] = useState(false)
  const inputRef = useRef()
  const debouncedValue = useDebounce(value, 250)
  const hasDebouncedValueChangeOnce = useRef(false) // to ignore suggest on first render
  const isBtnOkDisplayed = isGE() && route === ROUTE_SUGGEST && !loading
  const isQuickSearch = isGE() && !hasFocus && (!value || value === '') && isFirstStepToFill
  const inputId = `SearchInput${idx}`
  const { showUI } = useUIDisplay(UI_ELEMENTS.SUGGEST)
  const isHomePage = useSelector(selectIsHomePage)

  const onSubmit = useCallback(() => {
    execSetCurrentSearch(value)
    return Promise.resolve().then(() => blur())
  }, [value])

  useImperativeHandle(ref, () => {
    return { onSubmit }
  })

  useEffect(() => {
    if (__BROWSER__) setIsReady(true)
  }, [__BROWSER__])

  useEffect(() => {
    if (route !== prevRoute && isPagelessRoute(prevRoute) && !isPagelessRoute(route)) {
      blur()
    }
  }, [route])

  const doSuggest = val => {
    execSetCurrentSearch(val)
    return execSuggest({ terms: val, suggestFilterType })
  }

  useEffect(() => {
    if (hasDebouncedValueChangeOnce.current && hasFocus) {
      doSuggest(debouncedValue)
    }
    hasDebouncedValueChangeOnce.current = true
  }, [hasFocus, debouncedValue])

  const handleChange = event => {
    onChange && onChange({ value: event.target.value })
  }

  const handleFocus = useCallback(
    e => {
      setTimeout(() => {
        if (value.length !== 0) e.target.select()
      }, 0)
      if (navigateToSuggestOnFocus) execNavigateTo({ route: ROUTE_SUGGEST })
      showUI()
      onFocus && onFocus()
      setFocus(true)
    },
    [execNavigateTo, showUI, doSuggest, onFocus, setFocus]
  )

  const blur = useCallback(() => {
    inputRef?.current?.blur()
    setFocus(false)
  }, [inputRef, setFocus])

  return (
    <div
      className={classNames(
        isHomePage ? s.homePageContainer : s.container,
        s.container,
        hasIcon && s.withIcon,
        className
      )}
    >
      {prefix && (
        <label className={classNames(s.prefix)} htmlFor={inputId}>
          {prefix}
        </label>
      )}
      {hasIcon && <SearchInputIcon />}
      <input
        ref={inputRef}
        type="text"
        id={inputId}
        value={value}
        className={classNames(s.field, isHomePage && hasGreenPlaceholder ? s.greenPlaceholder : s.whitePlaceholder)}
        placeholder={placeholder}
        onFocus={handleFocus}
        onChange={handleChange}
        disabled={!isReady}
        autoComplete="off"
      />
      {loading && (
        <span className={s.throbber}>
          <Throbber />
        </span>
      )}
      {isBtnOkDisplayed && (
        <TagActionButton
          className={s.btnOk}
          tagInfo={{ tag: PUB_TAG.PUB_FOCUS_CHAMP_SAISI }}
          type={TAG_PUBLISHER}
          shouldTagImpression={true}
          isSubmitButton={true} // allows form to be submitted
        >
          OK
        </TagActionButton>
      )}
      {isQuickSearch && <QuickSearchInput onChange={onChange} searchInputRef={inputRef} />}
    </div>
  )
})

SearchInput.displayName = 'SearchInput'
export default SearchInput
